// Standard stat ids: the attribute names every combat, item, status, class, enemy and difficulty preset references,
// so a status preset's "attack × 1.15" means the same thing in every game that uses it. The values (bases, clamps,
// growth) stay the game's or a stat-block preset's; this list only fixes the names and what they mean.
import { checkVocabulary, type VocabularyIssue, type VocabularyOptions } from "./vocabulary.js";

/** The stat vocabulary's version. */
export const STATS_CONTRACT_VERSION = "1.0.0" as const;

/** What kind of number a stat is. A pool is spent and refilled up to its max; a max caps a pool; a rating is compared
 *  or added (attack, defence); a multiplier scales something (1 is normal); a chance is 0..1. */
export type StatKind = "pool" | "max" | "rating" | "multiplier" | "chance";

/** One standard stat. */
export interface StandardStat {
  id: string;
  kind: StatKind;
  /** A pool's max stat, when it has one. */
  max?: string;
  meaning: string;
}

/** The standard stats, in a fixed order. British spelling (`defence`), as the engine's docs use. */
export const STANDARD_STATS = [
  { id: "hp", kind: "pool", max: "hpMax", meaning: "Health; 0 is defeat" },
  { id: "hpMax", kind: "max", meaning: "Most health" },
  { id: "mana", kind: "pool", max: "manaMax", meaning: "Spent by spells and skills (MP)" },
  { id: "manaMax", kind: "max", meaning: "Most mana" },
  { id: "stamina", kind: "pool", max: "staminaMax", meaning: "Spent by sprinting, dodging, blocking and heavy blows" },
  { id: "staminaMax", kind: "max", meaning: "Most stamina" },
  { id: "poise", kind: "pool", max: "poiseMax", meaning: "Broken by blows; at 0 the character staggers" },
  { id: "poiseMax", kind: "max", meaning: "Most poise" },
  { id: "energy", kind: "pool", meaning: "A meter that fills in play and pays for the big move" },
  { id: "attack", kind: "rating", meaning: "Physical damage dealt" },
  { id: "defence", kind: "rating", meaning: "Physical damage taken away" },
  { id: "magic", kind: "rating", meaning: "Magical damage and healing dealt" },
  { id: "resistance", kind: "rating", meaning: "Magical damage taken away" },
  { id: "speed", kind: "rating", meaning: "Turn order and action rate (initiative, agility)" },
  { id: "luck", kind: "rating", meaning: "Nudges chances: drops, criticals, status rolls" },
  { id: "accuracy", kind: "rating", meaning: "Chance to hit, where a game rolls hits" },
  { id: "evasion", kind: "rating", meaning: "Chance to be missed, where a game rolls hits" },
  { id: "critChance", kind: "chance", meaning: "Chance a blow is critical, 0..1" },
  { id: "critMult", kind: "multiplier", meaning: "Damage scale on a critical blow" },
  { id: "moveScale", kind: "multiplier", meaning: "Scale on movement speed (1 normal; a slow is below 1)" },
  { id: "attackSpeed", kind: "multiplier", meaning: "Scale on attack animation and recovery speed" },
] as const satisfies readonly StandardStat[];

/** A standard stat's id. */
export type StandardStatId = (typeof STANDARD_STATS)[number]["id"];

const BY_ID = new Map<string, StandardStat>(STANDARD_STATS.map((s) => [s.id, s]));
const IDS = new Set(BY_ID.keys());

/** The standard stat of that id, if any. */
export function standardStat(id: string): StandardStat | undefined {
  return BY_ID.get(id);
}

/** True for a standard stat id. */
export function isStandardStat(id: string): id is StandardStatId {
  return IDS.has(id);
}

/** Check a game's stat ids (its stat table, or the attributes a preset references): each is standard, an alias of
 *  one (`{ HP: "hp" }`) or the game's own. */
export function checkStatIds(ids: readonly string[], o: VocabularyOptions = {}): VocabularyIssue[] {
  return checkVocabulary("stat", IDS, ids, o);
}
